'use client';

import { useTranslation } from 'next-i18next';
import { type ActionItem, usePromptBarActions } from './usePromptBarActions';

export interface MissionNavItem {
  item: ActionItem;
  label: string;
}

/**
 * The mission destinations in display order (Jobs, Coach, Talents) plus the shared
 * click handler. Consumed by the mobile BottomNav and the desktop HeaderMissionLinks —
 * the two surfaces that replaced the mission chips.
 */
export const useMissionNavItems = (
  locale: Locale,
  isAuthenticated: boolean,
  // MyJobs+ and Add job as plain entries, appended last. On by consumers that list them
  // (the `+` popup, the mobile BottomNav); the desktop header leaves this off —
  // MainMenuLinks renders both there.
  includeMyJobs = false,
): { items: MissionNavItem[]; handleClick: (item: ActionItem) => void } => {
  const { t } = useTranslation('common');
  // The mode param is not used by usePromptBarActions — 'chat' is an arbitrary valid value.
  const { candidateItems, companyItems, handleClick } = usePromptBarActions(locale, 'chat', isAuthenticated);
  const itemsByKey = new Map([...candidateItems, ...companyItems].map((item) => [item.key, item]));

  const items = [
    { item: itemsByKey.get('jobs'), label: t('mc.chip.jobs') },
    { item: itemsByKey.get('coaching'), label: t('mc.chip.coach') },
    // Talents = talent search (/cv/search, hard navigation — see usePromptBarActions).
    // Stories left the menu entirely; /stories stays reachable by direct link only.
    { item: itemsByKey.get('talents'), label: t('header.label.cv_search') },
    ...(includeMyJobs ? [
      { item: itemsByKey.get('candidate-management'), label: t('label.my.jobs.plus') },
      { item: itemsByKey.get('add-job'), label: t('header.label.add_jobs') },
    ] : []),
  ].filter((entry): entry is MissionNavItem => Boolean(entry.item));

  return { items, handleClick };
};
